<script setup lang="ts">
/**
 * BaseModal — Reusable modal component
 * Sprint 2 Design System B2
 *
 * Usage:
 *   <BaseModal v-model="showModal" title="Edit Item" size="lg">
 *     <template #default>Body content</template>
 *     <template #footer>Footer buttons</template>
 *   </BaseModal>
 */
import { watch, onUnmounted, nextTick, ref, computed } from 'vue'
import { X } from 'lucide-vue-next'
import { useLayoutMode } from '@/core/composables/useLayoutMode'

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

const props = withDefaults(defineProps<{
  modelValue: boolean
  title?: string
  size?: ModalSize
  closable?: boolean
  persistent?: boolean  // If true, clicking backdrop doesn't close
  fullscreenOnTablet?: boolean  // If true, go fullscreen on touch devices
}>(), {
  title: '',
  size: 'md',
  closable: true,
  persistent: false,
  fullscreenOnTablet: false,
})

const { isTouchMode } = useLayoutMode()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  close: []
}>()

const modalContent = ref<HTMLElement | null>(null)

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[95vw] max-h-[95vh]',
}

function close() {
  if (!props.closable) return
  emit('update:modelValue', false)
  emit('close')
}

function onBackdropClick() {
  if (!props.persistent) {
    close()
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.closable && !props.persistent) {
    close()
  }
}

// Lock body scroll when modal is open
watch(() => props.modelValue, (open) => {
  if (open) {
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeydown)
    nextTick(() => {
      modalContent.value?.focus()
    })
  } else {
    document.body.style.overflow = ''
    document.removeEventListener('keydown', onKeydown)
  }
})

onUnmounted(() => {
  document.body.style.overflow = ''
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="modelValue"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/50 backdrop-blur-sm"
          @click="onBackdropClick"
        />

        <!-- Modal -->
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="modelValue"
            ref="modalContent"
            class="relative w-full bg-white shadow-2xl dark:bg-slate-800"
            :class="[
              fullscreenOnTablet && isTouchMode
                ? 'fixed inset-0 rounded-none max-w-none max-h-none flex flex-col'
                : ['rounded-xl', sizeClasses[size]]
            ]"
            :style="fullscreenOnTablet && isTouchMode ? 'padding-top: env(safe-area-inset-top, 0px)' : ''"
            role="dialog"
            aria-modal="true"
            tabindex="-1"
          >
            <!-- Header -->
            <div
              v-if="title || closable || $slots.header"
              class="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 shrink-0"
              :class="fullscreenOnTablet && isTouchMode ? 'px-4 py-3' : 'px-6 py-4'"
            >
              <slot name="header">
                <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
                  {{ title }}
                </h3>
              </slot>
              <button
                v-if="closable"
                type="button"
                class="rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
                :class="fullscreenOnTablet && isTouchMode ? 'p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center' : 'p-1.5'"
                @click="close"
              >
                <X :class="fullscreenOnTablet && isTouchMode ? 'h-6 w-6' : 'h-5 w-5'" />
              </button>
            </div>

            <!-- Body -->
            <div
              :class="fullscreenOnTablet && isTouchMode ? 'px-4 py-4 flex-1 overflow-y-auto' : 'px-6 py-4'"
            >
              <slot />
            </div>

            <!-- Footer -->
            <div
              v-if="$slots.footer"
              class="flex items-center justify-end gap-3 border-t border-slate-200 dark:border-slate-700 shrink-0"
              :class="fullscreenOnTablet && isTouchMode ? 'px-4 py-3' : 'px-6 py-4'"
            >
              <slot name="footer" />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
